import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, AlertTriangle, Upload, FileText, X, Rocket } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Question } from "@/types/test";
import { useAuth } from "@/contexts/AuthContext";
import { EmployerSignupFields, EmployerFormData } from "@/components/auth/EmployerSignupFields";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { extractTextFromFile, validateFileType, type ExtractedJDContent } from "@/components/job/utils/jdExtraction";
import { generateQuestions } from "@/components/test-creator/utils/questionGeneration";
interface CreateJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuestions?: Question[];
  onComplete?: (employerData: any) => Promise<void>;
}

export function CreateJobDialog({ 
  open, 
  onOpenChange,
  initialQuestions,
  onComplete 
}: CreateJobDialogProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isQuickCreating, setIsQuickCreating] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  const [employerData, setEmployerData] = useState<EmployerFormData>({
    organization: "",
    companySize: "1-10",
    industry: "",
    companyDescription: ""
  });
  
  const handleEmployerDataChange = (data: Partial<EmployerFormData>) => {
    setEmployerData(prev => ({ ...prev, ...data }));
  };
  
  const [isEmployerSubmitting, setIsEmployerSubmitting] = useState(false);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [authError, setAuthError] = useState("");
  
  const [mode, setMode] = useState<'job-description' | 'employer-signup'>(
    user ? 'job-description' : 'employer-signup'
  );

  // JD Upload states
  const [isUploadingJD, setIsUploadingJD] = useState(false);
  const [uploadedJDContent, setUploadedJDContent] = useState<ExtractedJDContent | null>(null);

  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [typingIndex, setTypingIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const placeholderTexts = [
    "I want to hire a junior software engineer for my tech consulting firm in London.",
    "I want to hire a senior mechanical engineer for my aluminum processing factory in Mumbai.",
    "I need a marketing manager with 5+ years experience in digital marketing for my e-commerce startup in Toronto.",
    "Looking for a UX designer with healthcare industry experience for our medical software company in Berlin."
  ];
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (mode !== 'job-description' || !isTyping) return;
    
    const currentText = placeholderTexts[currentPlaceholderIndex];
    
    if (typingIndex < currentText.length) {
      const typingTimeout = setTimeout(() => {
        setCurrentPlaceholder(prev => prev + currentText[typingIndex]);
        setTypingIndex(typingIndex + 1);
      }, 25);
      
      return () => clearTimeout(typingTimeout);
    } else {
      const pauseTimeout = setTimeout(() => {
        setIsTyping(false);
        setTimeout(() => {
          setCurrentPlaceholder("");
          setTypingIndex(0);
          setCurrentPlaceholderIndex((currentPlaceholderIndex + 1) % placeholderTexts.length);
          setIsTyping(true);
        }, 2000);
      }, 1500);
      
      return () => clearTimeout(pauseTimeout);
    }
  }, [mode, typingIndex, currentPlaceholderIndex, isTyping, placeholderTexts]);

  const handleEnhance = async () => {
    if (!user) {
      navigate('/signup', { 
        state: { 
          returnTo: '/test-creator',
          jobDetails: {
            description: description,
          }
        }
      });
      return;
    }

    onOpenChange(false);
    navigate('/test-creator', {
      state: {
        jobDetails: {
          description: description,
        },
        initialQuestions: initialQuestions || []
      }
    });
  };

  const handleSignUp = async () => {
    if (!email || !password || !employerData.organization) {
      setAuthError("Please fill in all required fields");
      return;
    }

    if (password.length < 6) {
      setAuthError("Password must be at least 6 characters");
      return;
    }

    try {
      setIsSigningUp(true);
      setAuthError("");
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            organization: employerData.organization,
            company_size: employerData.companySize,
            industry: employerData.industry,
            role: 'employer'
          }
        }
      });

      if (error) throw error;
      
      console.log("[CreateJobDialog] Signup successful - User metadata:", data?.user?.user_metadata);
      
      sessionStorage.setItem('pendingEmployerData', JSON.stringify(employerData));
      
      sessionStorage.setItem('isCreatingTest', 'true');
      
      if (onComplete) {
        await onComplete(employerData);
      }
      
      toast({
        title: "Account created",
        description: "Please check your email to confirm your account",
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error('[CreateJobDialog] Error signing up:', error);
      setAuthError(error.message || "Failed to create account");
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleEmployerSubmit = async () => {
    if (!employerData.organization) {
      toast({
        title: "Missing Information",
        description: "Please enter your organization name",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      if (!email || !password) {
        setAuthError("Email and password are required to create an account");
        return;
      }
      
      await handleSignUp();
      return;
    }

    try {
      setIsEmployerSubmitting(true);
      
      if (onComplete) {
        await onComplete(employerData);
      }
      
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error submitting employer data:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save employer information",
        variant: "destructive",
      });
    } finally {
      setIsEmployerSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      console.log("[CreateJobDialog] Initiating Google signup as employer");
      
      sessionStorage.setItem('intendedRole', 'employer');
      sessionStorage.setItem('isCreatingTest', 'true');
      sessionStorage.setItem('enforceEmployerRole', 'true');
      
      const { error, data } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/role-selection`,
          queryParams: {
            role: 'employer'
          }
        }
      });
      
      if (error) {
        console.error("[CreateJobDialog] Google signup error:", error);
        throw error;
      }
      
      console.log("[CreateJobDialog] Google signup initiated successfully:", data);
    } catch (err: any) {
      console.error("[CreateJobDialog] Google signup error:", err);
      toast({
        title: "Signup Error",
        description: err.message || "Failed to sign up with Google",
        variant: "destructive",
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && description.trim()) {
        handleEnhance();
      }
    }
  };

  // JD Upload handlers
  const handleJDUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await processJDFile(file);
      }
    };
    input.click();
  };

  const processJDFile = async (file: File) => {
    if (!validateFileType(file)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a DOCX file.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingJD(true);
      
      // Extract text from file
      const extractedContent = await extractTextFromFile(file);
      setUploadedJDContent(extractedContent);
      
      // Set the raw extracted text directly (no AI processing)
      setDescription(extractedContent.text);
      
      toast({
        title: "JD Uploaded Successfully",
        description: `Extracted content from ${file.name}. You can review and edit below.`,
      });

    } catch (error: any) {
      console.error('Error processing JD file:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to extract text from the job description file.",
        variant: "destructive",
      });
      setUploadedJDContent(null);
    } finally {
      setIsUploadingJD(false);
    }
  };

  const clearUploadedJD = () => {
    setUploadedJDContent(null);
    setDescription("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-3xl bg-card p-4 sm:p-6 ${isMobile ? 'w-[95vw] max-h-[90vh] overflow-y-auto' : ''}`}>
        {mode === 'employer-signup' ? (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <DialogTitle className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2 text-foreground">Create an Account</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Please create an account to publish your test.
              </DialogDescription>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              {!user && (
                <>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="bg-background text-foreground placeholder:text-muted-foreground border-border"
                    />
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input 
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password (min. 6 characters)"
                      className="bg-background text-foreground placeholder:text-muted-foreground border-border"
                    />
                  </div>
                </>
              )}
              
              <EmployerSignupFields 
                data={employerData}
                onChange={handleEmployerDataChange}
              />
              
              {authError && (
                <div className="text-sm text-red-500 font-medium">
                  {authError}
                </div>
              )}
              
              {!user && (
                <div className="text-xs text-muted-foreground mt-2">
                  By signing up, you agree to our{" "}
                  <Link to="/privacy-policy" className="text-secondary hover:underline" onClick={() => onOpenChange(false)}>
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-and-conditions" className="text-secondary hover:underline" onClick={() => onOpenChange(false)}>
                    Terms & Conditions
                  </Link>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="bg-background hover:bg-accent text-foreground border-border"
                size={isMobile ? "sm" : "default"}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmployerSubmit}
                disabled={(!user && (!email || !password)) || !employerData.organization || isEmployerSubmitting || isSigningUp}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                size={isMobile ? "sm" : "default"}
              >
                {(isEmployerSubmitting || isSigningUp) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {user ? "Complete Registration" : "Sign Up & Publish"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <DialogTitle className="text-xl sm:text-2xl font-bold mb-0 text-foreground">Create Candidate Test</DialogTitle>
            </div>
            
            
            <div className="bg-secondary/10 p-3 sm:p-4 rounded-sm text-foreground">
              <p className="text-xs sm:text-sm leading-relaxed">
                Provide details about the job role and the skills you want to assess. 
                We'll generate a test with multiple choice, essay, audio, and video 
                response questions to filter candidates effectively.
              </p>
            </div>
            
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="description" className="text-foreground text-sm">
                Enter job role details and required skills:
              </Label>
              
              {/* Show uploaded file info */}
              {uploadedJDContent && (
                <div className="flex items-center gap-2 p-2 bg-secondary/20 rounded-lg">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium flex-1">{uploadedJDContent.fileName}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearUploadedJD}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
              
              <div className="relative">
                <Textarea
                  id="description"
                  ref={textAreaRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={uploadedJDContent ? "AI-extracted content loaded above. You can edit it here..." : currentPlaceholder}
                  onKeyDown={handleKeyDown}
                  className="h-[120px] sm:h-[150px] transition-all duration-500 bg-background text-foreground placeholder:text-muted-foreground/70 border-border focus:border-secondary text-sm"
                />
                {isUploadingJD && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Extracting text...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2 flex-wrap">
              <Button
                variant="outline"
                onClick={handleJDUpload}
                disabled={isUploadingJD}
                className="bg-background hover:bg-accent text-foreground border-border flex-1 sm:flex-none"
                size={isMobile ? "sm" : "default"}
              >
                {isUploadingJD && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Upload className={`${isUploadingJD ? 'hidden' : 'mr-2'} h-4 w-4`} />
                Upload JD
              </Button>
              <Button
                ref={generateButtonRef}
                onClick={handleEnhance}
                disabled={isLoading}
                className="bg-secondary hover:bg-secondary/90 text-secondary-foreground flex-1 sm:flex-none"
                size={isMobile ? "sm" : "default"}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Sparkles className={`${isLoading ? 'hidden' : 'mr-2'} h-4 w-4`} />
                Generate Test
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
